import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Row,
  Col,
  Typography,
  Space,
  message,
} from "antd";
import { FileTextOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useCreateLeaveRequest } from "../../../query/HrmQuery";
import { AxiosError } from "axios";
import { notify } from "../../../utils/toast";
import { ILeaveRequestQuery } from "../../../models/Hrm";

const { Text } = Typography;

interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  query: ILeaveRequestQuery;
}

export const ModalAddLeaveRequest: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  query,
}) => {
  const createLeave = useCreateLeaveRequest();
  const [form] = Form.useForm();

  const startDate = Form.useWatch("startDate", form);
  const endDate = Form.useWatch("endDate", form);
  const values = Form.useWatch([], form);
  const [submittable, setSubmittable] = useState(false);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  useEffect(() => {
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const diffDays = end.isAfter(start) ? end.diff(start, "day") + 1 : 0;
      form.setFieldValue("totalDays", diffDays);
    }
  }, [startDate, endDate, form]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      form.setFieldsValue({
        type: "PLAN",
        startDate: dayjs(),
        endDate: dayjs().add(1, "day"),
        totalDays: 1,
      });
    }
  }, [isOpen, form]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        type: values.type.toUpperCase(),
      };
      await createLeave.mutateAsync({
        data: payload,
        id: "",
        query,
      });
      message.success("Tạo đơn xin nghỉ thành công");
      setIsOpen(false);
      form.resetFields();
    } catch (error: unknown) {
      const err = error as AxiosError<{ message: string }>;
      const message =
        err.response?.data?.message || "Tạo đơn xin nghỉ thất bại";
      notify.error(message);
    }
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf("month");
  };

  const disabledTime = () => {
    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < 24; i++) {
          if (i < 8 || i > 18) hours.push(i);
        }
        return hours;
      },
      disabledMinutes: (selectedHour: number) => {
        const minutes = [];
        if (selectedHour === 8) {
          for (let i = 0; i < 30; i++) minutes.push(i);
        } else if (selectedHour === 18) {
          for (let i = 1; i < 60; i++) minutes.push(i);
        }
        return minutes;
      },
    };
  };

  return (
    <Modal
      title={<Typography.Title level={4}>Tạo đơn xin nghỉ</Typography.Title>}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      onOk={() => {
        if (createLeave.isPending) return;
        form.submit();
      }}
      okButtonProps={{ disabled: !submittable }}
      okText="Gửi đơn xin nghỉ"
      confirmLoading={createLeave.isPending}
      cancelText="Hủy bỏ"
      width={700}
      centered
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Vui lòng điền đầy đủ thông tin bên dưới
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="type"
              label={
                <Space>
                  <FileTextOutlined className="text-blue-500" />
                  <Text strong>Loại nghỉ</Text>
                </Space>
              }
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn loại nghỉ",
                },
              ]}
            >
              <Select>
                <Select.Option value="PLAN">
                  Nghỉ theo kế hoạch (Plan)
                </Select.Option>

                <Select.Option value="SUDDENTY">
                  Nghỉ đột xuất (Suddently)
                </Select.Option>

                <Select.Option value="EARLY">Về sớm (Early)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label={
                <Space>
                  <CalendarOutlined className="text-blue-500" />
                  <Text strong>Ngày bắt đầu</Text>
                </Space>
              }
              dependencies={["endDate"]}
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn ngày bắt đầu",
                },

                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const endDate = getFieldValue("endDate");

                    if (
                      !value ||
                      !endDate ||
                      dayjs(value).isBefore(dayjs(endDate))
                    ) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error("Ngày bắt đầu phải nhỏ hơn ngày kết thúc"),
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endDate"
              dependencies={["startDate"]}
              label={
                <Space>
                  <CalendarOutlined className="text-blue-500" />
                  <Text strong>Ngày kết thúc</Text>
                </Space>
              }
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn ngày kết thúc",
                },

                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue("startDate");

                    if (
                      !value ||
                      !startDate ||
                      dayjs(value).isAfter(dayjs(startDate))
                    ) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error("Ngày kết thúc phải lớn hơn ngày bắt đầu"),
                    );
                  },
                }),
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%" }}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              shouldUpdate={(prev, current) => prev.reason !== current.reason}
            >
              {() => (
                <Form.Item
                  name="detail"
                  label={<Text strong>Ghi chú chi tiết</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng nhập ghi chú chi tiết",
                    },
                  ]}
                >
                  <Input.TextArea rows={3} />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>{" "}
    </Modal>
  );
};
